/**
 * UserStatsCard Component
 * Professional dashboard card showing user statistics for superadmin
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUserStats,
  selectUserStats,
  selectUserStatsLoading,
  selectUserStatsError,
  selectPagination,
  selectSearchQuery,
  setSearchQuery,
  setPage,
  clearError
} from '../store/slices/userStatsSlice';
import {
  UsersIcon,
  UserPlusIcon,
  ChartBarIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const UserStatsCard = () => {
  const dispatch = useDispatch();
  const { users, summary, lastUpdated } = useSelector(selectUserStats);
  const loading = useSelector(selectUserStatsLoading);
  const error = useSelector(selectUserStatsError);
  const pagination = useSelector(selectPagination);
  const searchQuery = useSelector(selectSearchQuery);

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showDetails, setShowDetails] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initial load only
  useEffect(() => {
    if (!hasInitialized) {
      dispatch(fetchUserStats({
        page: 1,
        limit: 5,
        search: ''
      }));
      setHasInitialized(true);
    }
  }, [dispatch, hasInitialized]);

  // Debounced search - auto-search while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchQuery) {
        dispatch(setSearchQuery(localSearch));
        dispatch(fetchUserStats({
          page: 1,
          limit: pagination.limit,
          search: localSearch
        }));
      }
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(timer);
  }, [localSearch]); // Only depend on localSearch

  const handleRefresh = () => {
    dispatch(fetchUserStats({
      page: pagination.page,
      limit: pagination.limit,
      search: searchQuery
    }));
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    dispatch(setSearchQuery(''));
    dispatch(fetchUserStats({
      page: 1,
      limit: pagination.limit,
      search: ''
    }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
    dispatch(fetchUserStats({
      page: newPage,
      limit: pagination.limit,
      search: searchQuery
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTopInstitutionTypes = (user) => {
    const breakdown = user.institutionTypeBreakdown || {};
    return Object.entries(breakdown)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type, count]) => ({ type, count }));
  };

  if (loading && !users.length) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 py-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm mr-3">
              <ChartBarIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight">User Activity Stats</h3>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm hover:scale-105 active:scale-95"
            title="Refresh Stats"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {/* Error State */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => dispatch(clearError())}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="px-6 py-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300">
                <UsersIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-gray-900 mb-1">{summary.totalUsers}</span>
              <p className="text-sm text-gray-600 font-medium">Total Users</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300">
                <UserPlusIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-gray-900 mb-1">{summary.usersWithContacts}</span>
              <p className="text-sm text-gray-600 font-medium">Active Contributors</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-gray-900 mb-1">{summary.totalContactsCreated}</span>
              <p className="text-sm text-gray-600 font-medium">Total Contacts</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-br from-amber-500 to-yellow-600 p-3 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300">
                <TrophyIcon className="h-6 w-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-gray-900 mb-1">
                {summary.topContributor?.totalContacts || 0}
              </span>
              <p className="text-sm text-gray-600 font-medium">Top Contributor</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Contributors */}
      <div className="px-6 py-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <h4 className="text-lg font-bold text-gray-900">Top Contributors</h4>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {loading && localSearch && (
              <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
          {localSearch && (
            <p className="text-xs text-gray-500 mt-1">
              Searching as you type...
            </p>
          )}
        </div>

        <div className="space-y-3">
          {users.map((user, index) => (
            <div key={user.id} className="flex flex-col sm:flex-row items-center sm:items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] group gap-4">
              <div className="flex items-center w-full sm:flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shadow-md transition-transform duration-300 group-hover:scale-110 ${index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                    index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white' :
                        'bg-gradient-to-br from-blue-400 to-blue-500 text-white'
                    }`}>
                    #{index + 1}
                  </div>
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">{user.name}</p>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                  <div className="flex flex-wrap items-center mt-2 gap-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-sm ${user.role === 'superadmin' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                      user.role === 'admin' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                        'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                      }`}>
                      {user.role}
                    </span>
                    {user.lastContactCreated && (
                      <span className="text-[10px] sm:text-xs text-gray-400 font-medium whitespace-nowrap">
                        Last: {formatDate(user.lastContactCreated)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 w-full sm:w-auto flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 pt-3 sm:pt-0 border-gray-100">
                <div className="flex items-center sm:flex-col sm:items-end gap-2 sm:gap-0">
                  <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{user.totalContacts}</div>
                  <div className="text-xs text-gray-500 font-medium">contacts</div>
                </div>
                {Object.keys(user.institutionTypeBreakdown).length > 0 && (
                  <div className="flex flex-wrap sm:flex-col gap-1 sm:gap-1 sm:mt-2 justify-end">
                    {getTopInstitutionTypes(user).map(({ type, count }) => (
                      <div key={type} className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                        {type}: {count}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {users.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <UsersIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No user statistics available</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastUpdated && (
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center font-medium">
            Last updated: {formatDate(lastUpdated)}
          </p>
        </div>
      )}
    </div>
  );
};

export default UserStatsCard;